'use client'

import { type ReactNode, useMemo } from 'react'
import { OptimizelyProvider, createInstance } from '@optimizely/react-sdk'
import { LOG_LEVEL } from '@optimizely/optimizely-sdk'
import type { UserInfo } from '@optimizely/react-sdk/dist/utils'
import { useSiteContext } from '@dustin-web/microsite-components/src/hooks'
import { createOptimizelyUserAttributes } from '../utils/optimizely-user-attributes'
import { useAuth } from '@dustin-web/microsite-components/src/auth'
import { createOptimizelyEventDispatcher } from '../utils/optimizely-event-dispatcher'
import { useCookieState } from '@dustin-web/microsite-components/src/hooks/use-cookie-state'
import { serverSettings } from '@dustin-web/microsite-config'

type Props = {
  children: ReactNode
  datafile: string | undefined
}

export function FeatureFlagsProvider({ children, datafile }: Props) {
  const { anonymousUserIdentifier, site, punchOutTicket, isMobile, deviceType, userAgent } =
    useSiteContext()
  const { cookies } = useCookieState()
  const { userData } = useAuth()

  const hasAnalyticsConsent = cookies.consent.analytics === 'true'

  const optimizelyClientInstance = useMemo(
    () =>
      createInstance({
        ...(datafile
          ? { datafile: JSON.parse(datafile) }
          : { sdkKey: serverSettings.optimizelySdkKey }),
        eventDispatcher: createOptimizelyEventDispatcher(hasAnalyticsConsent),
        eventFlushInterval: 1000,
        eventBatchSize: 10,
        logLevel: LOG_LEVEL.ERROR,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [datafile, hasAnalyticsConsent]
  )

  const userContext: Required<UserInfo> = {
    id: anonymousUserIdentifier,
    attributes: createOptimizelyUserAttributes(
      site,
      punchOutTicket,
      isMobile,
      deviceType,
      userAgent,
      userData ?? {}
    ),
  }

  return (
    <OptimizelyProvider optimizely={optimizelyClientInstance} user={userContext}>
      {children}
    </OptimizelyProvider>
  )
}

export default FeatureFlagsProvider
